/**
 * Grafik paleti — DOĞRULANMIŞ değerler.
 *
 * Bu palet göz kararıyla seçilmedi; dataviz doğrulayıcısıyla altı kontrolden
 * (lightness bandı, chroma tabanı, CVD ayrımı, normal görüş tabanı, yüzey
 * kontrastı) hem açık hem koyu temada 6/6 PASS alarak seçildi.
 *
 * KURALLAR — ihlali inceleme reddi sebebidir:
 *  1. Kategorik renk SIRAYA göre değil VARLIĞA göre atanır. Bir nokta
 *     filtrelenip çıkarıldığında kalan noktaların rengi DEĞİŞMEZ.
 *  2. 6. seri için yeni renk ÜRETİLMEZ; "Diğer"e katlanır veya küçük
 *     çoklu (small multiples) kullanılır.
 *  3. Durum renkleri (onay/uyarı/hata) kategorik seri olarak KULLANILMAZ.
 *  4. Tek eksen. İki farklı ölçekli ölçüm için iki grafik veya ortak tabana
 *     endeksleme — ÇİFT EKSEN ASLA.
 *  5. Metin seri rengini giymez; değerler ve etiketler mürekkep tokenlarında kalır.
 */

/** Kategorik yuva sayısı. Bunun üzeri "Diğer". */
export const KATEGORIK_YUVA = 5;

/** CSS değişkeni referansları — tema değişimi yeniden render gerektirmez. */
export const GRAFIK_RENK = {
  kategorik: [
    "var(--gr-1)",
    "var(--gr-2)",
    "var(--gr-3)",
    "var(--gr-4)",
    "var(--gr-5)",
  ] as const,
  sirali: [
    "var(--gr-sira-1)",
    "var(--gr-sira-2)",
    "var(--gr-sira-3)",
    "var(--gr-sira-4)",
    "var(--gr-sira-5)",
  ] as const,
  arti: "var(--gr-arti)",
  orta: "var(--gr-orta)",
  eksi: "var(--gr-eksi)",
  izgara: "var(--gr-izgara)",
  eksen: "var(--gr-eksen)",
  yuzey: "var(--gr-yuzey)",
  murekkep: "var(--murekkep)",
  murekkep2: "var(--murekkep-2)",
  murekkep3: "var(--murekkep-3)",
  /**
   * Yön kodlaması. DİKKAT: bu konvansiyon TEK TEK TUTARLAR içindir
   * (bir defter satırı para giren mi çıkan mı). Toplam/trend gibi BİLEŞİK
   * işaretlerde kullanılmaz — aylık harcama trendini baştan aşağı kırmızıya
   * boyamak bilgi taşımaz, yalnızca alarm üretir.
   */
  giren: "var(--giren)",
  cikan: "var(--cikan)",
  marka: "var(--simit)",
} as const;

/**
 * Varlığa sabitlenmiş renk ataması.
 * Aynı anahtar (örn. nokta_id) her zaman aynı yuvayı alır; liste kısalsa bile.
 */
export function renkAtayici(
  anahtarlar: readonly string[],
): (anahtar: string) => string {
  const harita = new Map<string, string>();
  anahtarlar.slice(0, KATEGORIK_YUVA).forEach((a, i) => {
    harita.set(a, GRAFIK_RENK.kategorik[i]!);
  });
  return (anahtar: string) => harita.get(anahtar) ?? GRAFIK_RENK.murekkep3;
}

/** Büyüklük -> sıralı rampa yuvası (0..1 arası oranla). */
export function siraliRenk(oran: number): string {
  const i = Math.min(
    GRAFIK_RENK.sirali.length - 1,
    Math.max(0, Math.floor(oran * GRAFIK_RENK.sirali.length)),
  );
  return GRAFIK_RENK.sirali[i]!;
}

/** Kutuplaşma: artıda ise artı hue, eksideyse eksi hue, sıfırda nötr. */
export function kutupRenk(deger: string): string {
  if (deger.startsWith("-")) return GRAFIK_RENK.eksi;
  if (/^0+([.,]0+)?$/.test(deger.trim())) return GRAFIK_RENK.orta;
  return GRAFIK_RENK.arti;
}

/** Mark özellikleri — dataviz mark spesifikasyonu. */
export const MARK = {
  /** Çubuk uçları 4px yuvarlak, taban çizgisine yapışık */
  cubukKose: [4, 4, 0, 0] as [number, number, number, number],
  cubukKoseYatay: [0, 4, 4, 0] as [number, number, number, number],
  /** Çizgi 2px */
  cizgiKalinlik: 2,
  /** İşaretçi >= 8px */
  isaretciYaricap: 4,
  /** Dolgular arası 2px yüzey boşlluğu */
  bosluk: 2,
} as const;
